import React, { useEffect, useMemo } from 'react'
import { Reward } from '@vrw/data/src/redux/rewards/types'
import { RewardEarn } from '@vrw/data/src/redux/rewardsEarn/types'
import { rewardToCard } from '@vrw/data/src/utils/convertRewardToCard'
import { doAddRewardToWishlist, doRemoveRewardFromWishlist } from '@vrw/data/src/redux/wishlist/dispatchers'
import { media } from 'web/src/style/media'
import { useAppDispatch, useGetStore } from 'web/src/redux/hooks/hooks'
import { color } from 'web/src/style/variables'
import { IntroCarouselTile } from 'web/src/components/ContentfulBlock/IntroCarouselBlock/IntroCarouselTile'
import { LoadSpinner } from 'web/src/components'
import { map } from 'lodash/fp'
import { InfiniteData } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { reduceToPageCount } from '@/query/rewards/useInfiniteRewards'

const tileSize = {
  margin: 24,
} as const

export interface RewardsListProps {
  delayRender: boolean
  rewards: InfiniteData<RewardEarn[] | Reward[], unknown>
  isLoading?: boolean
  fetchNextPage: () => void
}

const itemsPerRow = {
  smallMobile: 1,
  mobile: 2,
  tablet: 3,
  desktop: 4,
}

export const InfinateRewardsList = ({ delayRender, fetchNextPage, rewards, isLoading }: RewardsListProps) => {
  const dispatch = useAppDispatch()

  const { ref: bottomInViewRef, inView: bottomInView } = useInView()

  const wishlist = useGetStore('wishlist.wishlist')
  const isLoadingWishlist = useGetStore('wishlist.isLoading')

  const rewardCards = useMemo(() => {
    return rewards.pages.flatMap((page) => map(rewardToCard(), page))
  }, [rewards.pages])

  const isOnWishlist = (rewardId: string) => {
    const reward = wishlist?.rewards.find((reward) => reward.rewardId === rewardId)
    return reward !== undefined
  }

  const handleOnWishlistClick = (rewardId: string, type: string) => {
    const wishlistType = type === 'reward' ? 'SPEND' : 'EARN'

    return isOnWishlist(rewardId)
      ? dispatch(doRemoveRewardFromWishlist(rewardId, wishlistType))
      : dispatch(doAddRewardToWishlist(rewardId, wishlistType))
  }

  useEffect(() => {
    if (bottomInView) fetchNextPage()
  }, [bottomInView, fetchNextPage])

  return (
    <>
      <style jsx>{`
        .tiles-wrapper {
          display: flex;
          flex-wrap: wrap;
          -webkit-box-pack: start;
          justify-content: flex-start;
          margin-top: 10px;
          margin-bottom: 24px;
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .tile-wrapper {
          animation: 0.7s fadeIn;
          position: relative;
          background-color: ${color.white};
          border: solid 1px ${color.secondaryButtonBorder};
          border-radius: 8px;
          margin-top: 16px;
          flex-direction: column;
          display: flex;
        }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }
        .tile-lister-spinner {
          align-items: center;
          display: flex;
          min-height: 200px;
        }
        .tile-wrapper :global(.inventory-card-logo) {
          width: 66px;
          height: 66px;
        }
        .tile-wrapper :global(.inventory-card-content) {
          height: auto;
          min-height: 157px;
        }
        @media ${media.mobileLargeAndLower} {
          .tile-wrapper :global(.inventory-card-content .header-three) {
            font-size: 20px !important;
          }
          .tile-wrapper :global(.inventory-card-content .body) {
            font-size: 16px !important;
          }
          .tile-wrapper :global(.inventory-card-content .inventory-card-points) {
            font-size: 20px !important;
          }
        }
        @media ${media.mobileLargeAndHigher} {
          .tiles-wrapper {
            margin-left: -24px;
            margin-bottom: 40px;
          }
          .tile-wrapper {
            width: calc(100% / ${itemsPerRow.mobile} - ${tileSize.margin}px);
            margin-left: 24px;
          }
        }
        @media ${media.tabletAndHigher} {
          .tile-wrapper {
            width: calc(100% / ${itemsPerRow.tablet} - ${tileSize.margin}px);
            margin-top: 24px;
          }
          .tile-wrapper :global(.inventory-card-logo) {
            width: 45px;
            height: 45px;
          }
          .tile-wrapper :global(.inventory-card-content) {
            height: auto;
            min-height: 166px;
          }
        }
        @media ${media.desktopAndHigher} {
          .tiles-wrapper {
            margin-bottom: 72px;
          }
          .tile-wrapper {
            width: calc(100% / ${itemsPerRow.desktop} - ${tileSize.margin}px);
          }
          .tile-wrapper :global(.inventory-card-logo) {
            width: 66px;
            height: 66px;
          }

          .tile-wrapper :global(.inventory-card-content) {
            height: auto;
            min-height: 229px;
          }
        }
      `}</style>
      {isLoading || delayRender ? (
        <div className="tile-lister-spinner">
          <LoadSpinner />
        </div>
      ) : (
        <>
          <output aria-live="polite" className="sr-only">
            ${reduceToPageCount(rewards.pages)} results returned
          </output>
          <div className="tiles-wrapper" role="listbox" aria-label="Rewards">
            {rewardCards.map((reward) => (
              <div className="tile-wrapper border" key={reward.rewardId}>
                <IntroCarouselTile
                  {...reward}
                  brandLogoMobileSize={66}
                  isWished={isOnWishlist(reward.rewardId)}
                  onWishListIconClick={() => handleOnWishlistClick(reward.rewardId, reward.type)}
                  isLoadingWishlist={isLoadingWishlist}
                  wishlistIconPosition={6}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <div ref={bottomInViewRef} />
    </>
  )
}
